import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import { parseMoney } from '../../api/parseMoney'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import { useEntitlement } from '../../platform/access/useEntitlement'
import type { components } from '../../api/generated/schema'
import { isEffectivelyDisconnected } from './accountConnection'

/**
 * An account in the shape the screen needs, not the wire's.
 *
 * **`currentCents`/`availableCents`/`openingCents` are RAW** — parsed by
 * `parseMoney` and nothing else. A liability's `currentCents` is still
 * POSITIVE here (the amount owed, trap 4 in the M5 spec); `signedCents` from
 * `accountMoney.ts` applies the display flip, and only at the point something
 * renders or totals a balance. Baking the flip in here would mean
 * `totalsOf` — which is asserted against the shared fixture in its own raw,
 * positive-owed convention (`accountMoney.test.ts`) — flips it a second time.
 */
export type ConnectionStatus = 'ok' | 'reauth_required' | 'disconnected'

/** Narrowed by switch, never cast: an unknown string is `null` (no claim made). */
function connectionStatusOf(raw: string | null | undefined): ConnectionStatus | null {
  switch (raw) {
    case 'ok':
    case 'reauth_required':
    case 'disconnected':
      return raw
    default:
      return null
  }
}

export type Account = {
  id: string
  name: string
  type: string
  subtype: string | null
  mask: string | null
  provider: string | null // 'manual' for hand-created
  // Populated for BOTH manual and linked accounts, not manual-only. The M5
  // plan's own "trap 1" is wrong here — verified against the backend's
  // `AccountResponse.from_account` (api/src/schemas/account.py:193-199,
  // confirmed against develop, 2026-09-11): a manual account reads its own
  // `institution_name` column, and a LINKED account reads it from its
  // `provider_item`, not straight off the account row. Both land in this one
  // field; nothing here has to special-case by provider.
  institutionName: string | null
  currentCents: number | null
  availableCents: number | null
  openingCents: number | null
  currency: string
  lastSyncedAt: Date | null
  ownerName: string | null
  // Alongside `ownerName`, not instead of it: `AccountDetail.tsx`'s owner
  // picker (Ticket 2) needs the id to preselect the current owner among
  // household members, since two members can share a display name.
  ownerId: string | null
  errorCode: string | null
  actionRequired: string | null
  // `provider_items.id` -- the id `/aggregator/items/{item_id}` takes. NOT
  // the same value as `ProviderItemResponse.provider_item_id`, which is the
  // aggregator's own external item id (#402, called out twice by the
  // backend). Unread today; #397 joins an account to its item through this.
  itemId: string | null
  /** The one source of connection health. `null` is a manual account, or a
   *  linked row whose item is gone altogether (the backend keeps it in totals). */
  connectionStatus: ConnectionStatus | null
  /** Offer Reconnect only when true; `disconnected` alone is not enough. */
  reconnectable: boolean
  /** Credit-card statement day, 1–31; `null` = not set (or not a credit card).
   *  Absent on the wire reads as `null`, never 0. */
  statementDay: number | null
}

/**
 * Carries the HTTP `status`, not just the parsed body — same shape as
 * `MerchantsError`/`CategoriesError`/`HouseholdError`. `openapi-fetch`'s
 * `error` is the response BODY; the status lives on the sibling `response`,
 * and Step 8's 409 needs both.
 */
export class AccountsError extends Error {
  readonly body: unknown
  readonly status: number

  constructor(body: unknown, status: number) {
    super(`Accounts request failed with ${status}`)
    this.name = 'AccountsError'
    this.body = body
    this.status = status
  }
}

/**
 * The one number `AccountsError.body` may carry on a manual-delete 409 —
 * `{ message, transaction_count }`, undocumented in `openapi.json` (only
 * 204/422 are declared for this endpoint) but real, confirmed against the
 * backend 2026-09-11. Read defensively rather than typed against the
 * generated client, and returns `null` — never a fabricated `0` — for
 * anything that is not exactly a number, so a caller renders a generic
 * message instead of a confident wrong count. The same refusal-to-guess
 * `utilisation` already applies to the credit meter (architect review, issue
 * #395 non-blocking). Lives here, not in a route component, so Ticket 2's
 * detail screen — which also deletes — reads the same function rather than
 * reimplementing the cast.
 */
export function transactionCountOf(body: unknown): number | null {
  if (typeof body !== 'object' || body === null) return null
  const count = (body as Record<string, unknown>).transaction_count
  return typeof count === 'number' ? count : null
}

function money(value: string | null | undefined): number | null {
  return value == null ? null : parseMoney(value)
}

export function toAccount(a: components['schemas']['AccountResponse']): Account {
  return {
    id: a.id,
    name: a.name,
    type: a.type,
    subtype: a.subtype ?? null,
    mask: a.mask ?? null,
    provider: a.provider ?? null,
    institutionName: a.institution_name ?? null,
    currentCents: money(a.current_balance),
    availableCents: money(a.available_balance),
    openingCents: money(a.opening_balance),
    currency: a.iso_currency_code,
    lastSyncedAt: a.last_synced_at != null ? new Date(a.last_synced_at) : null,
    ownerName: a.beneficial_owner?.display_name ?? a.beneficial_owner?.email ?? null,
    ownerId: a.beneficial_owner?.id ?? null,
    // Provider-neutral names (#402) -- plaid_item_error_code/
    // plaid_item_action_required are deprecated twins carrying the same
    // values, kept on the wire only until iOS and web have both switched.
    errorCode: a.item_error_code ?? null,
    actionRequired: a.item_action_required ?? null,
    itemId: a.item_id ?? null,
    connectionStatus: connectionStatusOf(a.connection_status),
    reconnectable: a.reconnectable,
    statementDay: a.statement_day ?? null,
  }
}

export async function listAccounts(): Promise<Account[]> {
  const { data, error, response } = await api.GET('/api/v1/accounts', { params: {} })
  if (error !== undefined || data === undefined) throw new AccountsError(error, response.status)
  return data.accounts.map(toAccount)
}

/**
 * How old a bank sync must be before the surface counts as behind rather than
 * current. `Async`'s `stale` state is bank-sync freshness (D20); a sync from
 * four minutes ago is not stale and one from six days ago is (M5 spec, Step
 * 5) — a single exported constant so the number is not scattered across the
 * module. 24 hours: well past a normal sync cadence, well short of "days".
 */
export const STALE_AFTER_MS = 24 * 60 * 60 * 1000

/**
 * The oldest `last_synced_at` among `accounts`, or `undefined` when there is
 * nothing to report — either because every account is manual (no sync at
 * all) or because the oldest one is still within `STALE_AFTER_MS`.
 *
 * `fromQuery`'s `asOf` decides `stale` vs `success` purely by whether it
 * returns a `Date` — there is no separate threshold hook in `Async` or
 * `fromQuery` itself, so the threshold has to be applied HERE, inside this
 * function, rather than merely computing a minimum and leaving the caller to
 * gate it.
 */
function oldestStaleSync(accounts: Account[]): Date | undefined {
  // An effectively disconnected account (`isEffectivelyDisconnected`: status
  // `disconnected`, or a linked row with no status) has a `last_synced_at`
  // frozen at the moment it was cut off, and its balance is left out of every
  // total, so it must not make the whole surface read stale forever.
  const synced = accounts
    .filter((a) => !isEffectivelyDisconnected(a))
    .map((a) => a.lastSyncedAt)
    .filter((d): d is Date => d !== null)
  if (synced.length === 0) return undefined

  const oldest = synced.reduce((min, d) => (d < min ? d : min))
  return Date.now() - oldest.getTime() > STALE_AFTER_MS ? oldest : undefined
}

/**
 * M5-R31/D-M5-6: a free user gets exactly ONE manual account; the SECOND
 * attempt is what surfaces the paywall, not the first. **The one exported
 * predicate for this rule** — architect review, #397 (B3): `Accounts.tsx`
 * and `Connect.tsx` each wrote their own inline expression, and they
 * disagreed (manual accounts only vs. every account), so the same
 * entitlement gated differently one click apart for a user with a linked
 * account and no manual one. `Connect.tsx`'s "every account" reading was
 * this codebase's own invention, unbacked by a requirement — M5-R31 names
 * manual accounts specifically, and Ticket 4 (RevenueCat, blocked) is what
 * owns deciding whether bank-linking ever gets its OWN, differently-named
 * cap. Until it does, one rule, read from one place.
 */
export function isOverFreeCap(accounts: Account[]): boolean {
  return accounts.filter((a) => a.provider === 'manual').length >= 1
}

/**
 * Should a new connection be gated — the ONE decision "Link a bank" and
 * "Reconnect" both read (owner decision on the reconnect issue: reconnecting is
 * gated exactly like linking, through the same `unlimited_accounts`
 * entitlement). Lifted out of `Connect.tsx` so the two cannot disagree.
 *
 * **Closed by default while the account list is unresolved**: `gated` reads
 * `true` for an unentitled user on `loading` and `error`, not just on a
 * resolved list past the cap. Reading the resolved array for every status
 * defaulted to `[]` while loading, which let an unentitled user over the cap
 * click through before the read settled. `empty` DOES resolve — a household
 * with no accounts is nobody over the cap.
 */
export function useAddAccountGate(): boolean {
  const entitled = useEntitlement('unlimited_accounts')
  const state = useAccounts()
  const resolved = state.status === 'success' || state.status === 'stale' || state.status === 'empty'
  const accounts = state.status === 'success' || state.status === 'stale' ? state.data : []
  return !entitled && (!resolved || isOverFreeCap(accounts))
}

/**
 * The account list — M5's first `synced` surface (`surfaces.ts`).
 *
 * `isEmpty` is reachable: a brand-new household genuinely has zero accounts,
 * and that is the gateway state this module exists to get someone out of.
 */
export function useAccounts(): AsyncState<Account[]> {
  const query = useQuery({
    queryKey: keys.accounts.list(),
    queryFn: listAccounts,
  })
  return fromQuery(query, {
    message: () => 'Accounts could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
    isEmpty: (accounts) => accounts.length === 0,
    asOf: oldestStaleSync,
  })
}

async function getAccount(id: string): Promise<Account> {
  const { data, error, response } = await api.GET('/api/v1/accounts/{account_id}', {
    params: { path: { account_id: id } },
  })
  if (error !== undefined || data === undefined) throw new AccountsError(error, response.status)
  return toAccount(data)
}

/**
 * One account, for Ticket 2's detail screen. `id: string | undefined` —
 * `useParams()`'s own typing, unforced, matching `useMerchant`'s reasoning:
 * the router types a param as possibly absent, and `enabled` parks the query
 * rather than firing with an empty id.
 *
 * Passes `asOf`, same as `useAccounts` above — so whichever surface renders
 * this MUST be registered `synced` in `surfaces.ts` (architect review, issue
 * #395). `<Async>` throws if a `stale` state reaches a `live` surface (rule
 * 10); the account-detail route is the reason this hook exists and has not
 * registered a surface yet, so this is a landmine for Ticket 2 to step past
 * rather than into.
 */
export function useAccount(id: string | undefined): AsyncState<Account> {
  const query = useQuery({
    queryKey: keys.accounts.detail(id ?? ''),
    queryFn: () => getAccount(id as string),
    enabled: id !== undefined,
  })
  return fromQuery(query, {
    message: () => 'Account could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
    asOf: (account) => oldestStaleSync([account]),
  })
}

/**
 * `DELETE /accounts/{id}` — for BOTH providers, and they mean different
 * things (M5-R19, M5 spec §Delete vs disconnect):
 *
 *   - **Manual**: a real soft delete that cascades to its transactions.
 *     `?confirm_delete_transactions=true` is required whenever the account
 *     HAS live transactions; without it the response is **409** carrying
 *     `{ message, transaction_count }` — undocumented in `openapi.json`
 *     today (only 204/422 are declared there) but real and confirmed against
 *     the backend, 2026-09-11. That real number is what lets the caller
 *     prompt with an actual count instead of generic copy.
 *   - **Linked**: Plaid cannot unlink one account of an Item, so this sets
 *     `is_hidden=true` instead — no 409, no transaction count. The caller's
 *     job is to say so BEFORE the request, not after: telling someone their
 *     account was "deleted" when it will resync on the next sync is a lie
 *     they catch themselves.
 *
 * `confirmDeleteTransactions` is omitted from the query entirely when falsy,
 * matching every other optional query param in this app — sending
 * `confirm_delete_transactions=false` and omitting it are not the same
 * request to a server that only checks truthiness on presence.
 */
export function useDeleteAccount(): {
  remove: (id: string, confirmDeleteTransactions?: boolean) => Promise<void>
  isPending: boolean
} {
  const client = useQueryClient()
  const mutation = useMutation({
    mutationFn: async ({
      id,
      confirmDeleteTransactions,
    }: {
      id: string
      confirmDeleteTransactions: boolean
    }) => {
      const { error, response } = await api.DELETE('/api/v1/accounts/{account_id}', {
        params: {
          path: { account_id: id },
          query: confirmDeleteTransactions ? { confirm_delete_transactions: true } : {},
        },
      })
      if (error !== undefined) throw new AccountsError(error, response.status)
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.accounts.all() })
    },
  })

  return {
    remove: (id, confirmDeleteTransactions = false) =>
      mutation.mutateAsync({ id, confirmDeleteTransactions }),
    isPending: mutation.isPending,
  }
}

/**
 * `PATCH /accounts/{id}/beneficial-owner` — Ticket 2's owner picker.
 *
 * `ownerId: null` clears it (`SetBeneficialOwnerRequest.beneficial_owner_id`
 * is nullable on the wire for exactly that). The response is
 * `BeneficialOwnerResponse`, not `AccountResponse` — it carries the sync
 * owner too, which `Account` has no field for — so this invalidates rather
 * than trying to map it back onto the cached account; `useAccount`/
 * `useAccounts` re-fetch and `toAccount` picks the new `ownerName`/`ownerId`
 * up from there, the same one-mapper discipline as every other mutation in
 * this file.
 */
export function useSetBeneficialOwner(): {
  setOwner: (id: string, ownerId: string | null) => Promise<void>
  isPending: boolean
} {
  const client = useQueryClient()
  const mutation = useMutation({
    mutationFn: async ({ id, ownerId }: { id: string; ownerId: string | null }) => {
      const { error, response } = await api.PATCH('/api/v1/accounts/{account_id}/beneficial-owner', {
        params: { path: { account_id: id } },
        body: { beneficial_owner_id: ownerId },
      })
      if (error !== undefined) throw new AccountsError(error, response.status)
    },
    onSuccess: (_data, { id }) => {
      void client.invalidateQueries({ queryKey: keys.accounts.detail(id) })
      void client.invalidateQueries({ queryKey: keys.accounts.list() })
    },
  })

  return {
    setOwner: (id, ownerId) => mutation.mutateAsync({ id, ownerId }),
    isPending: mutation.isPending,
  }
}

/**
 * `PATCH /accounts/{id}` carrying `statement_day` and NOTHING else — the
 * credit-card statement day, 1–31. An explicit `null` clears it (the backend
 * dumps with `exclude_unset`, so an OMITTED key means "unchanged" and would
 * make "Not set" a silent no-op). It must be a number: the backend rejects the
 * string `"15"`. `AccountUpdate` also accepts `is_hidden`, `opening_balance`
 * and `institution_name`; none of those is ever sent from here.
 *
 * Owner-only by the backend's query (`Account.user_id == current_user.id`), so
 * no client-side ownership check — `ownerId` is the BENEFICIAL owner and must
 * never gate this. A non-credit type answers 422 `statement_day_not_credit`.
 *
 * On success the account caches AND this account's statement cycles are
 * invalidated: the cycle boundaries move with the day.
 */
export function useSetStatementDay(): {
  setDay: (id: string, day: number | null) => Promise<Account>
} {
  const client = useQueryClient()
  const mutation = useMutation<
    Account,
    Error,
    { id: string; day: number | null },
    { previousDay: number | null | undefined; id: string }
  >({
    // Offline the PATCH is attempted and rejects at once; the default would
    // PAUSE it forever.
    networkMode: 'always',
    retry: false,
    mutationKey: keys.accounts.statementDayWrite(),
    // One write at a time: quick changes run in order, so the last pick wins on the server too.
    scope: { id: 'account-statement-day' },
    mutationFn: async ({ id, day }) => {
      const { data, error, response } = await api.PATCH('/api/v1/accounts/{account_id}', {
        params: { path: { account_id: id } },
        body: { statement_day: day },
      })
      if (error !== undefined || data === undefined) throw new AccountsError(error, response.status)
      return toAccount(data)
    },
    // Optimistic on the statementDay field ONLY, in the detail and the list:
    // the select is controlled by this cache, so it shows the pick at once.
    onMutate: async ({ id, day }) => {
      await Promise.all([
        client.cancelQueries({ queryKey: keys.accounts.detail(id) }),
        client.cancelQueries({ queryKey: keys.accounts.list() }),
      ])
      const previousDay = client.getQueryData<Account>(keys.accounts.detail(id))?.statementDay
      writeDay(client, id, day)
      return { previousDay, id }
    },
    // Restore only this field: a whole-snapshot restore would drop a
    // concurrent write to the same entry.
    onError: (_e, _vars, context) => {
      // A queued sibling holds a newer optimistic pick; restoring now would flash the old day.
      if (client.isMutating({ mutationKey: keys.accounts.statementDayWrite() }) > 1) return
      if (context?.previousDay !== undefined) writeDay(client, context.id, context.previousDay)
    },
    onSettled: (_data, _e, { id }) => {
      // A queued sibling write is still to run; refetching now would show the
      // server's older value over its optimistic one.
      if (client.isMutating({ mutationKey: keys.accounts.statementDayWrite() }) > 1) return
      void client.invalidateQueries({ queryKey: keys.accounts.all() })
      void client.invalidateQueries({ queryKey: keys.transactions.statementCyclesOf(id) })
    },
  })

  return { setDay: (id, day) => mutation.mutateAsync({ id, day }) }
}

function writeDay(client: QueryClient, id: string, day: number | null): void {
  client.setQueryData<Account>(keys.accounts.detail(id), (cur) => cur && { ...cur, statementDay: day })
  client.setQueryData<Account[]>(keys.accounts.list(), (cur) =>
    cur?.map((a) => (a.id === id ? { ...a, statementDay: day } : a)),
  )
}
